import { authAxios } from './axiosService';

// Session APIs are hosted on common service (REACT_APP_common_service_api_url),
// same host as authAxios base URL (REACT_APP_NEW_LOGIN_AUTH_URL).

export const ACTIVE_SESSIONS_STORAGE_KEY = 'activeSessionsData';
export const ACTIVE_SESSIONS_DISMISSED_KEY = 'activeSessionsPromptDismissed';

export function normalizeSessionsResponse(raw) {
  if (!raw) {
    return null;
  }

  const sessions = (raw.sessions || []).map((s) => ({
    sub: s.sub,
    sessionId: s.sub,
    isCurrent: s.isCurrent,
    deviceLabel: s.deviceLabel,
    userAgent: s.userAgent,
    deviceType: inferDeviceType(s.userAgent, s.deviceLabel),
  }));
  const currentSessionId = sessions.find((s) => s.isCurrent)?.sub || null;

  return {
    activeCount: raw.activeCount ?? sessions.length,
    currentSessionId,
    sessions,
  };
}

function inferDeviceType(userAgent = '', deviceLabel = '') {
  const haystack = `${userAgent} ${deviceLabel}`.toLowerCase();
  if (/iphone|ipad|android|mobile|ios/.test(haystack)) {
    return /ipad|tablet/.test(haystack) ? 'tablet' : 'mobile';
  }
  return 'desktop';
}

function parseSessionsApiResponse(res) {
  const normalized = normalizeSessionsResponse(res);
  if (normalized?.sessions) {
    return normalized;
  }
  return null;
}

export const readPersistedSessions = () => {
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_SESSIONS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const persistActiveSessions = (data) => {
  if (!data) {
    return;
  }
  window.sessionStorage.setItem(
    ACTIVE_SESSIONS_STORAGE_KEY,
    JSON.stringify(data),
  );
};

export const isActiveSessionsPromptDismissed = () =>
  window.sessionStorage.getItem(ACTIVE_SESSIONS_DISMISSED_KEY) === 'true';

export const dismissActiveSessionsPrompt = () => {
  window.sessionStorage.setItem(ACTIVE_SESSIONS_DISMISSED_KEY, 'true');
};

export const clearActiveSessionsStorage = () => {
  window.sessionStorage.removeItem(ACTIVE_SESSIONS_STORAGE_KEY);
  window.sessionStorage.removeItem(ACTIVE_SESSIONS_DISMISSED_KEY);
};

export const getOtherSessions = (sessions = [], currentSessionId) =>
  sessions.filter((session) => {
    const sessionKey = session.sub || session.sessionId;
    return sessionKey !== currentSessionId && session.isCurrent !== true;
  });

export const getActiveSessions = async () => {
  try {
    const res = await authAxios.get('/auth/v1/sessions');
    const data = parseSessionsApiResponse(res);
    if (data) {
      persistActiveSessions(data);
      return data;
    }
  } catch {
    // Sessions unavailable
  }

  return null;
};

export const revokeSession = async (sub) => {
  try {
    const res = await authAxios.delete(`/auth/v1/sessions/${sub}`);
    const data = parseSessionsApiResponse(res);
    if (data) {
      persistActiveSessions(data);
      return data;
    }
  } catch {
    throw new Error('Failed to revoke session');
  }

  return getActiveSessions();
};

export const revokeAllOtherSessions = async () => {
  try {
    const res = await authAxios.delete('/auth/v1/sessions/others');
    const data = parseSessionsApiResponse(res);
    if (data) {
      persistActiveSessions(data);
      return data;
    }
  } catch {
    throw new Error('Failed to revoke other sessions');
  }

  return getActiveSessions();
};
